import { Link } from "wouter";
import { Button } from "@workspace/ui-web/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Heart, ShieldCheck, Home as HomeIcon, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/seo";

export default function Landing() {
  useSeo({
    title: "Greater Hope Service Dogs | Service Dogs for People in Recovery",
    description:
      "We breed and train service dogs for people in early recovery from substance use disorder. Donate, sponsor a puppy, or become a volunteer puppy raiser.",
    path: "/",
  });
  const zeffyUrl = import.meta.env.VITE_ZEFFY_DONATION_URL as
    | string
    | undefined;
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground py-24 md:py-32">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
          <div className="container relative z-10 px-4 md:px-8 max-w-4xl text-center mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-balance leading-tight">
              Hope has a wet nose and a wagging tail.
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              We breed and train service dogs for people in early recovery from
              substance use disorder. Together, we build foundations of trust,
              responsibility, and unconditional love.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
              <Button
                asChild
                size="lg"
                className="bg-white text-primary hover:bg-white/90 font-semibold text-base"
              >
                <a href="#donate">Support Our Mission</a>
              </Button>
              <Link href="/apply">
                <Button
                  size="lg"
                  className="bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground border-primary-foreground/20 text-base"
                >
                  Become a Puppy Raiser
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Mission / Value Props */}
        <section className="py-24 bg-background">
          <div className="container px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                The Healing Power of Service Dogs
              </h2>
              <p className="text-muted-foreground text-lg">
                Recovery is a journey that shouldn't be walked alone. Our highly
                trained service dogs provide emotional regulation, routine, and
                a non-judgmental presence that is crucial during the fragile
                stages of early recovery.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-card p-8 rounded-2xl border text-center space-y-4 hover-elevate transition-all">
                <div className="mx-auto w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold">Trained to Support</h3>
                <p className="text-muted-foreground text-sm">
                  Our dogs undergo rigorous training to interrupt anxiety,
                  provide deep pressure therapy, and foster healthy emotional
                  regulation.
                </p>
              </div>
              <div className="bg-card p-8 rounded-2xl border text-center space-y-4 hover-elevate transition-all">
                <div className="mx-auto w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold">Unconditional Bond</h3>
                <p className="text-muted-foreground text-sm">
                  A service dog offers absolute acceptance, helping individuals
                  rebuild self-worth and learn healthy attachment.
                </p>
              </div>
              <div className="bg-card p-8 rounded-2xl border text-center space-y-4 hover-elevate transition-all">
                <div className="mx-auto w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
                  <HomeIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold">Community Raised</h3>
                <p className="text-muted-foreground text-sm">
                  Volunteers from our community raise these puppies in their
                  homes, building a network of care and shared purpose.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Raiser Call to Action */}
        <section className="py-24 bg-accent/30 border-y">
          <div className="container px-4 md:px-8 max-w-5xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Raise a Puppy, Change a Life.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Our volunteer puppy raisers are the heart of our program. You
                  open your home to a future service dog, teaching them basic
                  obedience and socializing them to the world. We provide the
                  training, support, and supplies. You provide the love.
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-primary" /> 14-18 month
                    commitment
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-primary" /> Weekly
                    training classes provided
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-primary" /> Make a
                    profound difference in someone's recovery
                  </li>
                </ul>
                <div className="pt-4">
                  <Link href="/apply">
                    <Button size="lg" className="w-full sm:w-auto">
                      Apply to be a Raiser
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl border">
                <img
                  src="https://images.unsplash.com/photo-1601614051061-39e2467dcfec?auto=format&fit=crop&q=80"
                  alt="Puppy training"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Donation Section */}
        <section id="donate" className="py-32 bg-white">
          <div className="container px-4 md:px-8 max-w-3xl mx-auto text-center">
            <div className="space-y-6 mb-12">
              <h2 className="text-4xl font-bold text-foreground">
                Fund the Journey
              </h2>
              <p className="text-xl text-muted-foreground">
                It costs over $25,000 to breed, raise, and train a single
                service dog. Your generous donation ensures that financial
                barriers never stand between a person in recovery and their
                lifeline.
              </p>
            </div>

            <div className="bg-card border shadow-sm rounded-3xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-primary"></div>

              {zeffyUrl ? (
                <div className="flex flex-col items-center justify-center space-y-6">
                  <Heart className="h-10 w-10 text-destructive" />
                  <h3 className="text-2xl font-semibold text-foreground">
                    Make a Donation
                  </h3>
                  <p className="text-base text-muted-foreground max-w-md mx-auto">
                    Every gift goes directly toward breeding, raising, and
                    training our service dogs. Donations are processed securely
                    through Zeffy.
                  </p>
                  <Button
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 mt-2 rounded-full px-8"
                  >
                    <a
                      href={zeffyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Donate via Zeffy
                    </a>
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center space-y-4 py-8">
                  <Heart className="h-10 w-10 text-destructive" />
                  <h3 className="text-2xl font-semibold text-foreground">
                    Online Giving Coming Soon
                  </h3>
                  <p className="text-base text-muted-foreground max-w-md mx-auto">
                    We are putting the finishing touches on secure online
                    donations through Zeffy. In the meantime, please reach out
                    to us directly to support our mission.
                  </p>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="mt-2 rounded-full px-8"
                  >
                    <a href="mailto:hello@greaterhopeservicedogs.org">
                      Contact Us to Give
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
